/**
 * A casa: o plano que a Ruth segue, e a chave que a mãe vira.
 *
 * É a tela que responde a uma pergunta que ela faz sozinha em voz alta — *por
 * que a Ruth acha que eu lavo roupa três vezes por semana?* — e deixa ela
 * discordar sem precisar conversar.
 *
 * A chave muda o plano de verdade: desligar tira da semana o que ainda ia ser
 * pedido. Por isso a tela devolve o que o servidor respondeu, e não o que ela
 * clicou.
 *
 * A linha inteira abre o conserto, e é ele que faz a seta do fim ser verdade.
 * Antes o toque prometia editar e entregava apagar: "lavar o banheiro toda
 * semana" não virava "a cada quinze dias", o nome digitado errado ficava
 * errado, e o único caminho era apagar e criar de novo — perdendo junto o
 * histórico do que ela já fez. É a regra 7: se a tela mostra uma coisa errada,
 * o conserto tem que caber na própria tela onde ela vê o erro.
 *
 * O "isso não é da minha casa" continua embaixo, que sempre foi o lugar dele:
 * é a saída para o que nunca foi dela, não o conserto do que está quase certo.
 *
 * Medidas de docs/design/Casa.dc.html.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  fraseDoErro,
  hhmm,
  type CorrecaoDeTarefa,
  type GrupoDaCasa,
  type TarefaDaCasa,
  type Frequencia,
  type TarefaDaCasaNova,
} from '../api'
import { Icone } from '../componentes/Icone'
import '../estilos/conserto.css'

type Props = {
  /** Mudar o plano muda o dia; quem mostra o dia precisa saber. */
  aoMudarOPlano: () => void
  /** Falar continua sendo o caminho; o convite do rodapé leva à Ruth. */
  aoFalarComARuth: () => void
}

const ABAS: { id: GrupoDaCasa; rotulo: string }[] = [
  { id: 'semana', rotulo: 'Toda semana' },
  { id: 'dia', rotulo: 'Todo dia' },
  { id: 'mes', rotulo: 'Todo mês' },
]

export function Casa({ aoMudarOPlano, aoFalarComARuth }: Props) {
  const [tarefas, setTarefas] = useState<TarefaDaCasa[] | null>(null)
  const [aba, setAba] = useState<GrupoDaCasa>('semana')
  const [virando, setVirando] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  /** Qual linha está aberta para conserto. Uma de cada vez. */
  const [corrigindo, setCorrigindo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      setTarefas((await api.casa()).tarefas)
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir o plano da casa'))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const virar = useCallback(
    async (tarefa: TarefaDaCasa) => {
      setVirando(tarefa.id)
      setErro(null)
      try {
        const nova = await api.virarAChave(tarefa.id, !tarefa.ativo)
        setTarefas((antes) => (antes ?? []).map((t) => (t.id === nova.id ? nova : t)))
        aoMudarOPlano()
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui mudar isso agora'))
      } finally {
        setVirando(null)
      }
    },
    [aoMudarOPlano],
  )

  /**
   * O erro sobe para o formulário de propósito: quem tentou salvar está olhando
   * para os campos, e a frase tem que aparecer ali, embaixo deles.
   */
  const corrigir = useCallback(
    async (tarefa: string, campos: CorrecaoDeTarefa) => {
      const nova = await api.corrigirTarefaDaCasa(tarefa, campos)
      // A rota devolve a tarefa inteira: trocar a linha pelo que o servidor
      // respondeu é o que faz "de 15 em 15 dias" aparecer ali mesmo, em vez de
      // uma esperança de que tenha dado certo.
      setTarefas((antes) => (antes ?? []).map((t) => (t.id === nova.id ? nova : t)))
      // Mudar a frequência muda a aba onde a tarefa mora. Sem seguir junto, a
      // linha que ela acabou de consertar sumiria da lista — e sumir se parece
      // demais com apagar, que é justamente o que este formulário veio evitar.
      setAba(nova.grupo)
      setCorrigindo(null)
      aoMudarOPlano()
    },
    [aoMudarOPlano],
  )

  const criar = useCallback(
    async (nova: TarefaDaCasaNova) => {
      await api.criarTarefaDaCasa(nova)
      setCriando(false)
      await carregar()
      aoMudarOPlano()
    },
    [carregar, aoMudarOPlano],
  )

  const tirar = useCallback(
    async (tarefa: string) => {
      await api.tirarTarefaDaCasa(tarefa)
      setCorrigindo(null)
      await carregar()
      aoMudarOPlano()
    },
    [carregar, aoMudarOPlano],
  )

  const daAba = (tarefas ?? []).filter((t) => t.grupo === aba)

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">A casa</h1>
          <span className="cabeca__data">o plano que a Ruth segue</span>
        </div>
      </header>

      <div className="abas">
        {ABAS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`aba ${item.id === aba ? 'aba--atual' : ''}`}
            aria-pressed={item.id === aba}
            onClick={() => {
              setAba(item.id)
              // Trocar de aba fecha o conserto aberto: o formulário some da
              // tela junto com a linha dele, e voltar e achá-lo aberto com
              // valores meio digitados é pior do que recomeçar.
              setCorrigindo(null)
            }}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      <div className="corpo">
        {erro && <p className="vazio">{erro}</p>}

        {tarefas && daAba.length === 0 && !erro && (
          <p className="vazio">Nada aqui ainda. Conta pra Ruth o que você faz.</p>
        )}

        {daAba.map((tarefa) => (
          <Linha
            key={tarefa.id}
            tarefa={tarefa}
            virando={virando === tarefa.id}
            corrigindo={corrigindo === tarefa.id}
            aoVirar={() => void virar(tarefa)}
            aoAbrir={() => setCorrigindo(corrigindo === tarefa.id ? null : tarefa.id)}
            aoFechar={() => setCorrigindo(null)}
            aoCorrigir={(campos) => corrigir(tarefa.id, campos)}
            aoTirar={() => tirar(tarefa.id)}
          />
        ))}

        {criando ? (
          <Criar aoCriar={criar} aoFechar={() => setCriando(false)} />
        ) : (
          <button type="button" className="convite" onClick={() => setCriando(true)}>
            <Icone nome="somar" tamanho={20} />
            Acrescentar uma coisa da casa
          </button>
        )}

        <button type="button" className="convite" onClick={aoFalarComARuth}>
          <Icone nome="microfone" tamanho={20} />
          Ou fala pra Ruth o que mais você faz
        </button>
      </div>
    </>
  )
}

function Linha({
  tarefa,
  virando,
  corrigindo,
  aoVirar,
  aoAbrir,
  aoFechar,
  aoCorrigir,
  aoTirar,
}: {
  tarefa: TarefaDaCasa
  virando: boolean
  corrigindo: boolean
  aoVirar: () => void
  aoAbrir: () => void
  aoFechar: () => void
  aoCorrigir: (campos: CorrecaoDeTarefa) => Promise<void>
  aoTirar: () => Promise<void>
}) {
  return (
    <div className={`plano ${tarefa.ativo ? '' : 'plano--fora'}`}>
      {/* Sem `aria-label`: aqui o nome dito por extenso já é a resposta da tela
          — "1× · sáb · você pediu" é o porquê de a tarefa estar no plano, e um
          rótulo curto apagaria justo isso de quem ouve. O `aria-expanded` conta
          o resto: que o toque abre uma coisa, e se ela já está aberta. */}
      <button
        type="button"
        className="plano__toque tocavel"
        aria-expanded={corrigindo}
        onClick={aoAbrir}
      >
        <span className="plano__texto">
          <span className="plano__nome">
            {tarefa.nome}
            {tarefa.de_quem && <span className="plano__de-quem">da {tarefa.de_quem}</span>}
          </span>
          <span className="plano__meta">{descrever(tarefa)}</span>
        </span>
        <span className="tocavel__seta">
          <Icone nome="seta" tamanho={16} />
        </span>
      </button>

      <button
        type="button"
        role="switch"
        aria-checked={tarefa.ativo}
        aria-label={`${tarefa.nome}: ${tarefa.ativo ? 'no plano' : 'fora do plano'}`}
        className={`chave ${tarefa.ativo ? 'chave--ligada' : ''}`}
        disabled={virando}
        onClick={aoVirar}
      >
        <span className="chave__bolinha" />
      </button>

      {corrigindo && (
        <Corrigir
          tarefa={tarefa}
          aoCorrigir={aoCorrigir}
          aoTirar={aoTirar}
          aoFechar={aoFechar}
        />
      )}
    </div>
  )
}

const DIAS_DA_CASA = [
  { codigo: 'seg', curto: 'seg' },
  { codigo: 'ter', curto: 'ter' },
  { codigo: 'qua', curto: 'qua' },
  { codigo: 'qui', curto: 'qui' },
  { codigo: 'sex', curto: 'sex' },
  { codigo: 'sab', curto: 'sáb' },
  { codigo: 'dom', curto: 'dom' },
]

type Cadencia = Frequencia

/**
 * Toda cadência que o plano conhece, com a palavra que a mãe usa.
 *
 * A tela lia isto de volta deduzindo — `grupo` mais a frase "de 15 em 15 dias"
 * vira quinzenal —, e na dedução `semestral` e `anual` não existiam (a mãe não
 * conseguia devolver a dedetização para "a cada seis meses") e `segue` aparecia
 * escrito "toda semana", que é falso. Agora a rota manda a frequência crua e
 * não há o que adivinhar.
 *
 * `segue` fica de fora da lista de propósito: "acontece depois de outra tarefa"
 * é uma relação entre duas coisas, não uma cadência, e escolher isso num select
 * exigiria dizer *depois de qual*. Quem tem `segue` vê a própria cadência
 * escrita e pode trocar por qualquer outra; só não pode voltar para `segue`
 * pela tela — para isso existe a conversa.
 */
const CADENCIAS: { valor: Cadencia; rotulo: string }[] = [
  { valor: 'diaria', rotulo: 'todo dia' },
  { valor: 'semanal', rotulo: 'toda semana' },
  { valor: 'quinzenal', rotulo: 'a cada 15 dias' },
  { valor: 'mensal', rotulo: 'todo mês' },
  { valor: 'trimestral', rotulo: 'a cada 3 meses' },
  { valor: 'semestral', rotulo: 'a cada 6 meses' },
  { valor: 'anual', rotulo: 'uma vez por ano' },
]

/**
 * "Depois de outra coisa" se lê, e não se escolhe.
 *
 * `segue` é uma relação entre duas tarefas — dobrar acontece depois de lavar —,
 * não uma cadência. Escolher isso num select sem dizer **de qual** coisa ela
 * segue não quer dizer nada, e a tarefa ficaria com uma frequência sem par.
 * Quem já tem `segue` vê a própria cadência escrita e pode trocar por qualquer
 * outra; para voltar a "segue" existe a conversa, que sabe perguntar de qual.
 */
const SO_DE_LEITURA: Record<string, string> = { segue: 'depois de outra coisa' }

/** Sempre na ordem da semana, para "sex, seg" e "seg, sex" serem a mesma coisa. */
function naOrdemDaSemana(dias: string[]): string[] {
  return DIAS_DA_CASA.map(({ codigo }) => codigo).filter((codigo) => dias.includes(codigo))
}

/**
 * Os campos de uma coisa da casa — os mesmos para acrescentar e para consertar.
 *
 * Moram juntos porque são a mesma pergunta feita duas vezes: dois layouts para
 * "o que é, com que frequência, em que dias, a que hora" seriam duas coisas
 * para a mãe aprender, e duas para divergirem no dia em que uma delas mudar.
 */
function CamposDaTarefa({
  nome,
  aoMudarNome,
  cadencia,
  aoMudarCadencia,
  opcoes,
  vezes,
  aoMudarVezes,
  dias,
  aoMudarDias,
  hora,
  aoMudarHora,
  autoFoco = false,
}: {
  nome: string
  aoMudarNome: (nome: string) => void
  cadencia: string
  aoMudarCadencia: (cadencia: string) => void
  opcoes: { valor: string; rotulo: string }[]
  /** Só o formulário de conserto pergunta isto; acrescentar começa com uma vez. */
  vezes?: number
  aoMudarVezes?: (vezes: number) => void
  dias: string[]
  aoMudarDias: (dias: string[]) => void
  hora: string
  aoMudarHora: (hora: string) => void
  autoFoco?: boolean
}) {
  return (
    <>
      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <input
          value={nome}
          onChange={(e) => aoMudarNome(e.target.value)}
          placeholder="Regar as plantas"
          maxLength={160}
          autoFocus={autoFoco}
        />
      </label>

      <label className="conf-pessoa__campo">
        <span>Com que frequência</span>
        <select value={cadencia} onChange={(e) => aoMudarCadencia(e.target.value)}>
          {opcoes.map(({ valor, rotulo }) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </label>

      {/* Quantas vezes só existe na semana: nas outras cadências o gerador
          marca uma vez por período, e um número aqui não mudaria nada. */}
      {aoMudarVezes && vezes !== undefined && cadencia === 'semanal' && (
        <label className="conf-pessoa__campo">
          <span>Quantas vezes na semana</span>
          <select value={vezes} onChange={(e) => aoMudarVezes(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6, 7].map((quantas) => (
              <option key={quantas} value={quantas}>
                {quantas === 1 ? '1 vez' : `${quantas} vezes`}
              </option>
            ))}
          </select>
        </label>
      )}

      <fieldset className="agenda__dias">
        <legend>Em algum dia certo?</legend>
        {DIAS_DA_CASA.map(({ codigo, curto }) => (
          <label key={codigo} className="agenda__dia-escolha">
            <input
              type="checkbox"
              checked={dias.includes(codigo)}
              onChange={(e) =>
                aoMudarDias(
                  naOrdemDaSemana(
                    e.target.checked ? [...dias, codigo] : dias.filter((d) => d !== codigo),
                  ),
                )
              }
            />
            {curto}
          </label>
        ))}
      </fieldset>

      <div className="plano__hora">
        <label className="conf-pessoa__campo">
          <span>Em alguma hora certa?</span>
          <input type="time" value={hora} onChange={(e) => aoMudarHora(e.target.value)} />
        </label>
        {/* O "x" do campo de hora do navegador some no celular, e sem ele a
            hora só sabia entrar. Tirar a hora é o que devolve a tarefa ao
            resumo da manhã, então precisa de um toque que ela ache. */}
        {hora && (
          <button type="button" className="conf-pessoa__deixar" onClick={() => aoMudarHora('')}>
            Tirar a hora
          </button>
        )}
      </div>
      <span className="conf-secao__nota">
        Só se for hora marcada mesmo. Sem hora eu encaixo onde couber e te conto de manhã,
        em vez de te interromper.
      </span>
    </>
  )
}

/**
 * Uma coisa da casa que o catálogo não conhece.
 *
 * A tela tinha uma chave por tarefa e mais nada: dava para desligar o que veio
 * pronto, e não dava para pôr o que a casa dela tem. Falar com a Ruth continua
 * sendo o caminho; isto é a saída.
 *
 * A hora é opcional de propósito, e a frase diz por quê: tarefa com hora vira
 * aviso, e a regra 4 é que silêncio é funcionalidade. Sem hora, o gerador
 * encaixa onde cabe e ela lê no resumo da manhã.
 */
function Criar({
  aoCriar,
  aoFechar,
}: {
  aoCriar: (nova: TarefaDaCasaNova) => Promise<void>
  aoFechar: () => void
}) {
  const [nome, setNome] = useState('')
  const [cadencia, setCadencia] = useState<Cadencia>('semanal')
  const [dias, setDias] = useState<string[]>([])
  const [hora, setHora] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !nome.trim()) return

    setSalvando(true)
    setErro(null)
    try {
      await aoCriar({
        nome: nome.trim(),
        frequencia: cadencia,
        dias,
        hora: hora ? `${hora}:00` : null,
      })
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui acrescentar agora.'))
      setSalvando(false)
    }
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <CamposDaTarefa
        nome={nome}
        aoMudarNome={setNome}
        cadencia={cadencia}
        aoMudarCadencia={(valor) => setCadencia(valor as Cadencia)}
        // Criar não oferece `segue`: ela ainda não disse de qual coisa segue.
        opcoes={CADENCIAS}
        dias={dias}
        aoMudarDias={setDias}
        hora={hora}
        aoMudarHora={setHora}
        autoFoco
      />

      {erro && (
        <p className="conf-erro" role="alert">
          {erro}
        </p>
      )}

      <div className="conf-pessoa__acoes">
        <button type="submit" className="botao-primario" disabled={salvando || !nome.trim()}>
          {salvando ? 'Acrescentando…' : 'Acrescentar'}
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Deixa pra lá
        </button>
      </div>
    </form>
  )
}

/**
 * O conserto de uma coisa da casa, já preenchido com o que a Ruth entendeu.
 *
 * Vem cheio de propósito, e o de acrescentar vem vazio: quase sempre ela quer
 * mexer numa coisa só, e um formulário em branco a obrigaria a redigitar o que
 * já estava certo.
 *
 * **Manda só o que ela mudou.** Não é economia de bytes: a rota marca a tarefa
 * como dita pela mãe em todo campo que chega, e o que é dito pela mãe o
 * catálogo não substitui mais. Reenviar o nome igual ao que já estava mudaria
 * o comportamento do gerador sem ela ter pedido nada.
 *
 * Abre a partir da linha, e a linha é a que ela estava lendo: o conserto mora
 * onde o erro aparece (regra 7).
 */
function Corrigir({
  tarefa,
  aoCorrigir,
  aoTirar,
  aoFechar,
}: {
  tarefa: TarefaDaCasa
  aoCorrigir: (campos: CorrecaoDeTarefa) => Promise<void>
  aoTirar: () => Promise<void>
  aoFechar: () => void
}) {
  const cadenciaOriginal = tarefa.frequencia
  // `vezes` vem do plano desta semana, não do que ficou guardado, e é esse
  // número que ela lê na linha. Preso entre 1 e 7 porque é o que a rota
  // aceita: uma tarefa fora dessa faixa abriria o formulário sem opção
  // marcada, e salvar mandaria uma mudança que ela não fez.
  const vezesOriginal = Math.min(7, Math.max(1, tarefa.vezes))
  const horaOriginal = tarefa.hora ? tarefa.hora.slice(0, 5) : ''

  const [nome, setNome] = useState(tarefa.nome)
  const [cadencia, setCadencia] = useState<Cadencia>(cadenciaOriginal ?? '')
  const [vezes, setVezes] = useState(vezesOriginal)
  const [dias, setDias] = useState<string[]>(naOrdemDaSemana(tarefa.dias))
  const [hora, setHora] = useState(horaOriginal)
  const [perguntando, setPerguntando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // A cadência que só se lê entra na lista como a primeira opção, para ela
  // poder trocar e voltar atrás sem fechar o formulário — mas não aparece em
  // tarefa que não a tem.
  const soDeLeitura = SO_DE_LEITURA[cadenciaOriginal]
  const opcoes: { valor: string; rotulo: string }[] = soDeLeitura
    ? [{ valor: cadenciaOriginal, rotulo: soDeLeitura }, ...CADENCIAS]
    : CADENCIAS

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !nome.trim()) return

    const campos: CorrecaoDeTarefa = {}
    const limpo = nome.trim()
    if (limpo !== tarefa.nome) campos.nome = limpo
    if (cadencia && cadencia !== cadenciaOriginal) campos.frequencia = cadencia
    if (cadencia === 'semanal' && vezes !== vezesOriginal) campos.vezes_por_periodo = vezes
    if (dias.join(',') !== naOrdemDaSemana(tarefa.dias).join(',')) campos.dias = dias
    // Tirar a hora não é o mesmo que não ter mexido nela, e uma hora nula não
    // sabe dizer a diferença — por isso o campo vazio vira `tirar_a_hora`. Sem
    // essa distinção, uma tarefa que ganhou hora nunca mais voltaria a não ter,
    // e tarefa sem hora é justamente a que não vira mais um alarme no dia dela.
    if (hora !== horaOriginal) {
      if (hora) campos.hora = `${hora}:00`
      else campos.tirar_a_hora = true
    }

    // Abriu para olhar e fechou: nada muda, e ninguém precisa saber que ela
    // abriu.
    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      await aoCorrigir(campos)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui salvar agora.'))
      setSalvando(false)
    }
  }

  const tirar = async () => {
    setErro(null)
    try {
      await aoTirar()
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui tirar agora.'))
      setPerguntando(false)
    }
  }

  return (
    <form className="plano__conserto" onSubmit={salvar}>
      <CamposDaTarefa
        nome={nome}
        aoMudarNome={setNome}
        cadencia={cadencia}
        aoMudarCadencia={(valor) => setCadencia(valor as Cadencia)}
        opcoes={opcoes}
        vezes={vezes}
        aoMudarVezes={setVezes}
        dias={dias}
        aoMudarDias={setDias}
        hora={hora}
        aoMudarHora={setHora}
      />

      {erro && (
        <p className="conf-erro" role="alert">
          {erro}
        </p>
      )}

      {perguntando ? (
        <>
          <p className="conf-secao__nota">
            A chave tira desta semana e guarda. Isto some de vez — é para o que nunca foi da
            sua casa. O que você já fez fica.
          </p>
          <div className="conf-pessoa__acoes">
            <button type="button" className="conf-pessoa__tirar" onClick={() => void tirar()}>
              Pode tirar
            </button>
            <button
              type="button"
              className="conf-pessoa__deixar"
              onClick={() => setPerguntando(false)}
            >
              Deixa aí
            </button>
          </div>
        </>
      ) : (
        <>
          <span className="conf-secao__nota">
            Mudando a frequência ou os dias, eu refaço o resto da semana a partir de hoje. O
            que você já fez fica.
          </span>
          <div className="conf-pessoa__acoes">
            <button type="submit" className="botao-primario" disabled={salvando || !nome.trim()}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
              Deixa como está
            </button>
            <button
              type="button"
              className="conf-pessoa__tirar-link"
              onClick={() => setPerguntando(true)}
            >
              Isso não é da minha casa
            </button>
          </div>
        </>
      )}
    </form>
  )
}

/** "3× · seg, qua, sex às 9h · você pediu" — só o que é verdade, e nada demais. */
function descrever(tarefa: TarefaDaCasa): string {
  // Numa aba chamada "Todo dia", "7× · seg, ter, qua, qui, sex, sab, dom" não
  // acrescenta nada: a mãe já sabe que é todo dia.
  const quando =
    tarefa.grupo === 'dia' ? ['todo dia'] : [contagem(tarefa), listaDeDias(tarefa)]

  return [...quando, tarefa.marcador].filter(Boolean).join(' · ')
}

function contagem(tarefa: TarefaDaCasa): string {
  const vezes = `${tarefa.vezes}×`
  return tarefa.cadencia ? `${vezes} ${tarefa.cadencia}` : vezes
}

function listaDeDias(tarefa: TarefaDaCasa): string {
  const dias = tarefa.dias.join(', ')
  if (!tarefa.hora) return dias
  return dias ? `${dias} às ${hhmm(tarefa.hora)}` : `às ${hhmm(tarefa.hora)}`
}
